/**
 * Phase 3–4 — Call-to-Arms spine.
 * Liege · call/answer both ways · will-they-show · garrison vs host · supply tease.
 * P4 Slice 2: horn inbox + seasonal multi-turn delayed banner replies.
 * P4 Slice 4: auto-resolve on quest arrival + return march; no battle preview UI; spy Proof still out of scope.
 */
import type { Season } from "./clock.ts";
import { createRng } from "./rng.ts";
import { hexDistance, parseKey, type Biome, type HexMap } from "./map.ts";
import {
  applyLosses,
  compositionFromHost,
  resolveBattle,
} from "./battle.ts";
import type { BannermanCard, LedgerState, StockMap } from "./economy.ts";
import {
  addMaa,
  addStacks,
  armsFromLegacy,
  cloneHostOrigin,
  cloneStacks,
  creditHostOrigin,
  emptyHostOrigin,
  emptyStacks,
  garrisonTotal,
  hostOriginTotal,
  hostTotal,
  mergeHostOrigin,
  mergeStacksInto,
  normalizeArms,
  normalizeHostOrigin,
  normalizeStacks,
  peelArmsStacksForLiegeSend,
  peelArmsStacksForLiegeSendDetail,
  peelFromStacks,
  peelHostOrigin,
  scaleHostOriginToTotal,
  stacksFromLegacy,
  stacksTotal,
  type HostOriginMap,
  type TroopStacks,
  type TypedArms,
  HOST_ORIGIN_SEAT,
} from "./troops.ts";
import {
  bannerDelayTurns,
  createHornInbox,
  postHorn,
  updateHornEntry,
  type HornInbox,
  type PendingBannerReply,
} from "./hornInbox.ts";
import type { LiegeQuest } from "./quest.ts";
import {
  beginQuestReturn,
  completeLiegeQuest,
  describeQuest,
  isQuestOpen,
  pickDefaultObjective,
  applyQuestSurvivorCount,
  startLiegeQuest,
  tickLiegeQuest,
} from "./quest.ts";
import {
  armyHostOrigin,
  armyStacks,
  syncArmyComposition,
  type ArmyToken,
} from "./march.ts";

export type { HornEntry, HornInbox, HornKind, PendingBannerReply } from "./hornInbox.ts";
export {
  bannerDelayTurns,
  createHornInbox,
  hornDigest,
  markAllHornsRead,
  markHornRead,
  pendingHornCount,
  postHorn,
  unreadHornCount,
  updateHornEntry,
  HORN_INBOX_CAP,
} from "./hornInbox.ts";

/** Biome under a hex key; plains if unknown. */
function biomeAt(
  map: HexMap | undefined,
  key: string | null | undefined,
  fallback: Biome = "plains",
): Biome {
  if (!map || !key) return fallback;
  const cell = map.cells.get(key);
  return (cell?.biome as Biome | undefined) ?? fallback;
}

export type LoyaltyBucket = "true" | "strained" | "rebel-risk";
/** join = full answer (all available spears: host + garrison). partial needs troopsSent override. */
export type CallOutcome = "join" | "delay" | "partial" | "refuse";
export type DistanceBand = "near" | "mid" | "far";
export type CallDirection = "you-to-banners" | "liege-to-you";

/** Turns granted when the player delays answering a liege call. */
export const DEFAULT_DELAY_TURNS = 2;
/** Favor hit for refusing the liege horn (significant). */
export const REFUSE_FAVOR_HIT = 12;
/** Mild favor hit while a delay is still open. */
export const DELAY_FAVOR_HIT = 2;
/** Favor hit for a partial levy (less than full). */
export const PARTIAL_FAVOR_HIT = 3;
/** Favor gain for a full answer. */
export const JOIN_FAVOR_GAIN = 4;

export const LOYALTY_LABELS: Record<LoyaltyBucket, string> = {
  true: "True",
  strained: "Strained",
  "rebel-risk": "Rebel risk",
};

export const OUTCOME_LABELS: Record<CallOutcome, string> = {
  join: "Full answer",
  delay: "Delay",
  partial: "Partial",
  refuse: "Refuse",
};

export interface Liege {
  name: string;
  title: string;
  /** How much the liege favors *you* (primary social number). */
  favor: number;
}

/**
 * Phase 4.5 typed arms: garrison/host are TroopStacks (MAA + promoted).
 * Prefer garrisonTotal()/hostTotal() for scalar HUD/math; keep stacks for roster.
 * Legacy scalar {garrison:n, host:n} is accepted via ensureCallState → armsFromLegacy.
 */
export type ArmsState = TypedArms;

export interface CallFactor {
  id: string;
  label: string;
  /** green | yellow | red */
  tone: "green" | "yellow" | "red";
  detail: string;
}

export interface ShowAssessment {
  loyalty: LoyaltyBucket;
  distance: number;
  distanceBand: DistanceBand;
  factors: CallFactor[];
  /** 0–1 join-leaning score before dice. */
  showScore: number;
  outcome: CallOutcome;
  reason: string;
}

export interface BannerAnswer {
  key: string;
  name: string;
  outcome: CallOutcome;
  reason: string;
  troopsSent: number;
  /** Spears still en route (multi-turn delay or far join). */
  troopsInTransit: number;
  /** Turns until delayed/far column arrives (P4 Slice 2). */
  delayTurnsLeft?: number;
  distance: number;
  distanceBand: DistanceBand;
  loyalty: LoyaltyBucket;
  showScore: number;
  factors: CallFactor[];
}

export interface PlayerAnswer {
  outcome: CallOutcome;
  reason: string;
  troopsSent: number;
  /** When outcome is delay: turns remaining before the call expires / must answer. */
  delayTurnsLeft?: number;
  loyalty: LoyaltyBucket;
  showScore: number;
  factors: CallFactor[];
}

/**
 * Per-bannerman seat arms (garrisoned / sent / en route).
 * Scalars stay as headcount mirrors; optional stacks* hold discrete unit types
 * (playtest residual: type + ownership survive call → march → release).
 */
export interface BannerSeatArms {
  key: string;
  name: string;
  /** Spears still at their hold (available to send). */
  garrisoned: number;
  /** Spears already arrived at the player's seat (in host contribution). */
  sent: number;
  /** Spears marching toward the player's seat. */
  inTransit: number;
  /** Typed composition at the hold (defaults to all-MAA of garrisoned). */
  stacksGarrisoned?: TroopStacks;
  /** Typed composition already arrived into player host from this banner. */
  stacksSent?: TroopStacks;
  /** Typed composition reserved / marching toward the seat. */
  stacksInTransit?: TroopStacks;
}

/** Sync scalar headcounts from typed stacks (or seed stacks from scalars). */
export function syncBannerSeatScalars(seat: BannerSeatArms): BannerSeatArms {
  const gStack =
    seat.stacksGarrisoned != null
      ? normalizeStacks(seat.stacksGarrisoned)
      : stacksFromLegacy(seat.garrisoned ?? 0);
  const sStack =
    seat.stacksSent != null
      ? normalizeStacks(seat.stacksSent)
      : stacksFromLegacy(seat.sent ?? 0);
  const tStack =
    seat.stacksInTransit != null
      ? normalizeStacks(seat.stacksInTransit)
      : stacksFromLegacy(seat.inTransit ?? 0);
  return {
    key: seat.key,
    name: seat.name,
    stacksGarrisoned: gStack,
    stacksSent: sStack,
    stacksInTransit: tStack,
    garrisoned: stacksTotal(gStack),
    sent: stacksTotal(sStack),
    inTransit: stacksTotal(tStack),
  };
}

export function normalizeBannerSeat(raw: Partial<BannerSeatArms> & { key: string; name: string }): BannerSeatArms {
  const garrisoned = Math.max(0, Math.floor(raw.garrisoned ?? 0));
  const sent = Math.max(0, Math.floor(raw.sent ?? 0));
  const inTransit = Math.max(0, Math.floor(raw.inTransit ?? 0));
  return syncBannerSeatScalars({
    key: raw.key,
    name: raw.name,
    garrisoned,
    sent,
    inTransit,
    stacksGarrisoned:
      raw.stacksGarrisoned != null ? normalizeStacks(raw.stacksGarrisoned) : stacksFromLegacy(garrisoned),
    stacksSent: raw.stacksSent != null ? normalizeStacks(raw.stacksSent) : stacksFromLegacy(sent),
    stacksInTransit:
      raw.stacksInTransit != null ? normalizeStacks(raw.stacksInTransit) : stacksFromLegacy(inTransit),
  });
}

export interface MusterResult {
  direction: CallDirection;
  answers: BannerAnswer[];
  playerAnswer?: PlayerAnswer;
  arms: ArmsState;
  /** Optional per-banner MAA patch (you-to-banners). */
  bannerSeats?: BannerSeatArms[];
  /** When set, replaces call.bannerSeats entirely after apply. */
  bannerSeatsReplace?: boolean;
  stocks: StockMap;
  supply: { food: number; gold: number };
  log: string[];
  /** Muster/answer note for HUD (not field odds; battle resolves on arrival). */
  preview: string;
  toast: string;
  /** Soft-fail: call blocked (e.g. already called this turn). */
  blocked?: boolean;
  /** New delayed banner columns from this muster (you-to-banners). */
  pendingBannerReplies?: PendingBannerReply[];
  /** Replace pending list when true (default merge by key). */
  pendingBannerRepliesReplace?: boolean;
  /** Horn inbox after this muster (optional; applyMuster posts if omitted). */
  /** Started/updated liege quest from this answer (join/partial). */
  activeQuest?: LiegeQuest | null;
  hornInbox?: HornInbox;
  /**
   * Host ownership ledger after this muster (you-to-banners / arrivals).
   * When set, applyMuster replaces call.hostOrigin.
   */
  hostOrigin?: HostOriginMap;
}

export interface CallState {
  liege: Liege;
  arms: ArmsState;
  /**
   * Who owns which typed stacks currently in host.
   * Banner keys → visitor stacks; HOST_ORIGIN_SEAT → player-owned host.
   */
  hostOrigin: HostOriginMap;
  /** Boot oath tutorial dismissed? */
  oathAccepted: boolean;
  /** Last muster / answer lines for HUD. */
  lastCallLog: string[];
  lastToast: string | null;
  lastPreview: string | null;
  lastAnswers: BannerAnswer[];
  lastPlayerAnswer: PlayerAnswer | null;
  /** Pending liege call the player has not answered (or still on delay clock). */
  liegeCallPending: boolean;
  callCount: number;
  /** Turn index of last liege horn posted (−1 = never). P4 Slice 5 cadence. */
  lastLiegeCallTurn: number;
  /** Turn index of last successful you-to-banners call (−1 = never). */
  lastBannerCallTurn: number;
  /** Turn index of last successful seat levy action (conscript OR discharge) (−1 = never). One levy/dismiss action per turn (XOR). */
  lastLevyTurn: number;
  /**
   * Seat levy/dismiss ordered this turn; finalizes on next End turn (not instant).
   * Conscript: able already reserved; garrison rises on resolve.
   * Discharge: garrison already peeled; able rises on resolve.
   */
  pendingLevy: PendingLevy | null;
  /** Per-bannerman garrisoned / sent / in-transit at their seats. */
  bannerSeats: BannerSeatArms[];
  /**
   * Open delay on a liege call: turns left to give a real answer.
   * null = no active delay clock.
   */
  liegeDelayTurnsLeft: number | null;
  /** Horn inbox — who called / answered / delayed / arrived (P4 Slice 2). */
  hornInbox: HornInbox;
  /** Bannermen who delayed; spears reserved, arrive after N End turns. */
  pendingBannerReplies: PendingBannerReply[];
  /** Open liege quest (host marching X→Y). null = none. */
  activeQuest: LiegeQuest | null;
}

/** Queued seat levy — resolves on End turn (analogous to banner in-transit). */
export type PendingLevy = {
  kind: "conscript" | "discharge";
  amount: number;
};

export const DEFAULT_LIEGE: Liege = {
  name: "Lord Halric",
  title: "Earl of the Westmarch",
  favor: 62,
};

export const DEFAULT_ARMS: ArmsState = armsFromLegacy(40, 0);

/** Starting garrison pool per bannerman seat (before any muster). */
export const BANNER_BASE_GARRISON = 18;

export function createBannerSeats(
  bannermen: { key: string; name: string }[],
): BannerSeatArms[] {
  return bannermen.map((b) =>
    normalizeBannerSeat({
      key: b.key,
      name: b.name,
      garrisoned: BANNER_BASE_GARRISON,
      sent: 0,
      inTransit: 0,
    }),
  );
}

export function createCallState(
  liege: Liege = DEFAULT_LIEGE,
  bannermen: { key: string; name: string }[] = [],
): CallState {
  return {
    liege: { ...liege },
    arms: normalizeArms(DEFAULT_ARMS),
    oathAccepted: false,
    lastCallLog: [],
    lastToast: null,
    lastPreview: null,
    lastAnswers: [],
    lastPlayerAnswer: null,
    liegeCallPending: false,
    callCount: 0,
    lastLiegeCallTurn: -1,
    lastBannerCallTurn: -1,
    lastLevyTurn: -1,
    pendingLevy: null,
    bannerSeats: createBannerSeats(bannermen),
    liegeDelayTurnsLeft: null,
    hornInbox: createHornInbox(),
    pendingBannerReplies: [],
    activeQuest: null,
    hostOrigin: emptyHostOrigin(),
  };
}

/**
 * Fill Slice-2 fields on older in-memory / future save blobs.
 * No disk save yet — keeps tick/muster/UI safe if a partial CallState appears.
 */
export function ensureCallState(call: CallState | (Partial<CallState> & object)): CallState {
  const base = call as Partial<CallState>;
  const fallback = createCallState(
    base.liege ? { ...DEFAULT_LIEGE, ...base.liege } : DEFAULT_LIEGE,
    base.bannerSeats?.map((s) => ({ key: s.key, name: s.name })) ?? [],
  );
  const hornRaw = base.hornInbox;
  const hornInbox: HornInbox =
    hornRaw && Array.isArray(hornRaw.entries)
      ? {
          entries: hornRaw.entries.map((e) => ({
            ...e,
            read: Boolean(e.read),
          })),
          nextId:
            typeof hornRaw.nextId === "number" && hornRaw.nextId > 0
              ? hornRaw.nextId
              : Math.max(1, hornRaw.entries.length + 1),
        }
      : createHornInbox();
  const pendingBannerReplies = Array.isArray(base.pendingBannerReplies)
    ? base.pendingBannerReplies.map((p) => ({ ...p }))
    : [];
  const bannerSeats = Array.isArray(base.bannerSeats)
    ? base.bannerSeats.map((s) =>
        normalizeBannerSeat({
          key: s.key,
          name: s.name,
          garrisoned: s.garrisoned,
          sent: s.sent,
          inTransit: s.inTransit,
          stacksGarrisoned: s.stacksGarrisoned,
          stacksSent: s.stacksSent,
          stacksInTransit: s.stacksInTransit,
        }),
      )
    : fallback.bannerSeats;
  // hostOrigin: prefer explicit; else empty (5b2 credits on muster/arrival)
  const hostOrigin = normalizeHostOrigin(
    (base as { hostOrigin?: unknown }).hostOrigin ?? fallback.hostOrigin,
  );
  return {
    ...fallback,
    ...base,
    liege: base.liege ? { ...DEFAULT_LIEGE, ...base.liege } : fallback.liege,
    arms: base.arms ? normalizeArms(base.arms) : fallback.arms,
    bannerSeats,
    lastCallLog: Array.isArray(base.lastCallLog) ? [...base.lastCallLog] : fallback.lastCallLog,
    lastAnswers: Array.isArray(base.lastAnswers) ? [...base.lastAnswers] : fallback.lastAnswers,
    hornInbox,
    pendingBannerReplies,
    oathAccepted: Boolean(base.oathAccepted),
    liegeCallPending: Boolean(base.liegeCallPending),
    callCount: typeof base.callCount === "number" ? base.callCount : 0,
    lastLiegeCallTurn:
      typeof base.lastLiegeCallTurn === "number" ? base.lastLiegeCallTurn : -1,
    lastBannerCallTurn:
      typeof base.lastBannerCallTurn === "number" ? base.lastBannerCallTurn : -1,
    lastLevyTurn: typeof base.lastLevyTurn === "number" ? base.lastLevyTurn : -1,
    pendingLevy: base.pendingLevy ?? null,
    liegeDelayTurnsLeft:
      base.liegeDelayTurnsLeft == null ? null : base.liegeDelayTurnsLeft,
    lastToast: base.lastToast ?? null,
    lastPreview: base.lastPreview ?? null,
    lastPlayerAnswer: base.lastPlayerAnswer ?? null,
    activeQuest: base.activeQuest ?? null,
    hostOrigin,
  };
}

/** Seat totals: player garrison + host, plus sum of banner garrisoned/sent/in-transit. */
export function seatArmsTotals(call: CallState): {
  playerGarrison: number;
  playerHost: number;
  bannersGarrisoned: number;
  bannersSent: number;
  bannersInTransit: number;
  realmSpears: number;
} {
  let bannersGarrisoned = 0;
  let bannersSent = 0;
  let bannersInTransit = 0;
  for (const s of call.bannerSeats) {
    bannersGarrisoned += s.garrisoned;
    bannersSent += s.sent;
    bannersInTransit += s.inTransit;
  }
  const g = garrisonTotal(call.arms);
  const h = hostTotal(call.arms);
  return {
    playerGarrison: g,
    playerHost: h,
    bannersGarrisoned,
    bannersSent,
    bannersInTransit,
    realmSpears: g + h + bannersGarrisoned + bannersInTransit,
  };
}


/**
 * Free able-bodied at the seat.
 * Post-0.3.10: ableBodied is free-pool only (military already left settlement).
 * `arms` kept for call-site compatibility; ignored.
 */
export function freeAbleSeat(ableBodied: number, _arms?: ArmsState): number {
  return Math.max(0, Math.floor(ableBodied));
}

/**
 * Free able-bodied at a banner.
 * ableBodied is free-pool only; seat arms kept for compatibility and ignored.
 */
export function freeAbleBanner(
  ableBodied: number,
  _seat?: Pick<BannerSeatArms, "garrisoned" | "sent" | "inTransit">,
): number {
  return Math.max(0, Math.floor(ableBodied));
}

/** True when seat already ran a successful levy action (conscript or discharge) this turn. */
export function alreadyLeviedThisTurn(call: CallState, turn: number): boolean {
  return call.lastLevyTurn === turn && turn >= 0;
}

export type LevyResult =
  | {
      ok: true;
      call: CallState;
      growth: import("./growth.ts").GrowthState;
      moved: number;
    }
  | {
      ok: false;
      reason: string;
      call: CallState;
      growth: import("./growth.ts").GrowthState;
      moved: 0;
    };

/**
 * Order garrison raise from free able-bodied at the seat.
 * Able is reserved immediately; garrison rises on next End turn (pendingLevy).
 * Gold cost 0. At most one successful levy action (conscript OR discharge) per turn when turn ≥ 0.
 */
export function conscriptToGarrison(
  call: CallState,
  growth: import("./growth.ts").GrowthState,
  n: number,
  opts?: { turn?: number },
): LevyResult {
  const want = Math.max(0, Math.floor(n));
  if (want <= 0) {
    return { ok: false, reason: "Nothing to conscript", call, growth, moved: 0 };
  }
  const turn = opts?.turn;
  if (
    turn !== undefined &&
    turn >= 0 &&
    alreadyLeviedThisTurn(call, turn)
  ) {
    return {
      ok: false,
      reason: "Already used levy/dismiss this turn — end turn first",
      call,
      growth,
      moved: 0,
    };
  }
  if (call.pendingLevy) {
    return {
      ok: false,
      reason: "Levy already ordered — end turn to finalize",
      call,
      growth,
      moved: 0,
    };
  }
  const free = freeAbleSeat(growth.seat.ableBodied, call.arms);
  if (free <= 0) {
    return {
      ok: false,
      reason: "No free able-bodied at seat",
      call,
      growth,
      moved: 0,
    };
  }
  const moved = Math.min(want, free);
  const able = Math.max(0, Math.floor(growth.seat.ableBodied) - moved);
  const gen = Math.max(0, Math.floor(growth.seat.genPop));
  const seat = {
    ...growth.seat,
    ableBodied: able,
    genPop: gen,
    population: gen + able,
  };
  return {
    ok: true,
    moved,
    call: {
      ...call,
      pendingLevy: { kind: "conscript", amount: moved },
      lastLevyTurn: turn !== undefined && turn >= 0 ? turn : call.lastLevyTurn,
      lastToast: `Ordered levy of ${moved} — joins garrison next End turn (${able} able free · ${garrisonTotal(call.arms)} garrison now).`,
    },
    growth: { ...growth, seat },
  };
}

/**
 * Order dismiss garrison → free able (returns to settlement next End turn).
 * Garrison peeled immediately; able restored on End turn. Gold 0.
 * Shares the one levy/dismiss action slot per turn with conscript (XOR).
 */
export function dischargeFromGarrison(
  call: CallState,
  growth: import("./growth.ts").GrowthState,
  n: number,
  opts?: { turn?: number },
): LevyResult {
  const want = Math.max(0, Math.floor(n));
  if (want <= 0) {
    return { ok: false, reason: "Nothing to discharge", call, growth, moved: 0 };
  }
  const turn = opts?.turn;
  if (
    turn !== undefined &&
    turn >= 0 &&
    alreadyLeviedThisTurn(call, turn)
  ) {
    return {
      ok: false,
      reason: "Already used levy/dismiss this turn — end turn first",
      call,
      growth,
      moved: 0,
    };
  }
  if (call.pendingLevy) {
    return {
      ok: false,
      reason: "Levy already ordered — end turn to finalize",
      call,
      growth,
      moved: 0,
    };
  }
  const gBefore = garrisonTotal(call.arms);
  if (gBefore <= 0) {
    return { ok: false, reason: "Garrison empty", call, growth, moved: 0 };
  }
  const moved = Math.min(want, gBefore);
  const peeled = peelFromStacks(call.arms.garrison, moved);
  const garr = stacksTotal(peeled.left);
  const ableNow = Math.max(0, Math.floor(growth.seat.ableBodied));
  return {
    ok: true,
    moved,
    call: {
      ...call,
      arms: { garrison: peeled.left, host: cloneStacks(call.arms.host) },
      pendingLevy: { kind: "discharge", amount: moved },
      lastLevyTurn:
        turn !== undefined && turn >= 0 ? turn : call.lastLevyTurn,
      lastToast: `Ordered dismiss of ${moved} — returns to able next End turn (${garr} garrison · ${ableNow} able free now).`,
    },
    growth,
  };
}

/** Alias: dismiss garrison spears back into the free able pool. */
export function dismissGarrisonToAble(
  call: CallState,
  growth: import("./growth.ts").GrowthState,
  n: number,
  opts?: { turn?: number },
): LevyResult {
  return dischargeFromGarrison(call, growth, n, opts);
}

/**
 * Release field host home.
 * No banners → all to seat garrison.
 * Else proportional to each banner's garrisoned (largest-remainder),
 * zero-garrison banners get nothing unless all are zero (then equal split).
 * Clears host. Host stays military (banner/seat garrison) — not able.
 * Use discharge/dismissGarrisonToAble to return spears to the free able pool.
 */

/**
 * Fold a quest column's discrete stacks + ownership into seat host.
 */
function foldArmyIntoHost(
  arms: ArmsState,
  hostOrigin: HostOriginMap,
  army: ArmyToken,
): { arms: ArmsState; hostOrigin: HostOriginMap; folded: number } {
  const stacks = armyStacks(army);
  const origin = armyHostOrigin(army);
  const folded = stacksTotal(stacks);
  if (folded <= 0) {
    return { arms, hostOrigin, folded: 0 };
  }
  return {
    arms: mergeStacksInto(arms, "host", stacks),
    hostOrigin: mergeHostOrigin(hostOrigin, origin),
    folded,
  };
}

export function releaseHostToBanners(
  call: CallState,
  growth: import("./growth.ts").GrowthState,
  amount?: number,
): LevyResult {
  const hostAll = hostTotal(call.arms);
  if (hostAll <= 0) {
    return { ok: false, reason: "Host is empty", call, growth, moved: 0 };
  }
  const host =
    amount === undefined
      ? hostAll
      : Math.max(0, Math.min(hostAll, Math.floor(amount)));
  if (host <= 0) {
    return { ok: false, reason: "Nothing to release", call, growth, moved: 0 };
  }

  // Origin-first restore: peel physical host + ownership ledger together.
  const peeled = peelFromStacks(call.arms.host, host);
  const hostLeftStacks = peeled.left;
  let remainStacks = cloneStacks(peeled.takenStacks);
  const peeledOrigin = peelHostOrigin(call.hostOrigin, host, []);
  let remainOrigin = cloneHostOrigin(peeledOrigin.takenByKey);
  const originLeft = peeledOrigin.origin;
  const phys = stacksTotal(remainStacks);
  if (hostOriginTotal(remainOrigin) !== phys) {
    if (phys <= 0) {
      remainOrigin = emptyHostOrigin();
    } else if (hostOriginTotal(remainOrigin) <= 0) {
      remainOrigin = creditHostOrigin(
        emptyHostOrigin(),
        HOST_ORIGIN_SEAT,
        remainStacks,
      );
    } else {
      remainOrigin = scaleHostOriginToTotal(remainOrigin, phys);
    }
  }

  const seatKeys = new Set(call.bannerSeats.map((s) => s.key));
  const addStacksByKey: Record<string, TroopStacks> = {};
  let toSeatStacks = emptyStacks();

  const debitRemain = (bag: TroopStacks) => {
    const next = cloneStacks(remainStacks);
    for (const id of Object.keys(bag) as (keyof TroopStacks)[]) {
      const n = Math.max(0, Math.floor(Number(bag[id] ?? 0)));
      if (n <= 0) continue;
      next[id] = Math.max(0, Math.floor(Number(next[id] ?? 0)) - n);
    }
    remainStacks = next;
  };

  // 1) Banner-owned → that banner's garrisoned stacks
  for (const key of Object.keys(remainOrigin).sort()) {
    if (key === HOST_ORIGIN_SEAT) continue;
    if (!seatKeys.has(key)) continue;
    const bag = remainOrigin[key];
    if (!bag || stacksTotal(bag) <= 0) continue;
    addStacksByKey[key] = addStacks(addStacksByKey[key] ?? emptyStacks(), bag);
    debitRemain(bag);
    delete remainOrigin[key];
  }

  // 2) Seat-owned → seat garrison
  const seatBag = remainOrigin[HOST_ORIGIN_SEAT];
  if (seatBag && stacksTotal(seatBag) > 0) {
    toSeatStacks = addStacks(toSeatStacks, seatBag);
    debitRemain(seatBag);
    delete remainOrigin[HOST_ORIGIN_SEAT];
  }

  // 3) Unknown origin keys + leftover physical → seat garrison
  for (const key of Object.keys(remainOrigin)) {
    const bag = remainOrigin[key];
    if (!bag || stacksTotal(bag) <= 0) continue;
    toSeatStacks = addStacks(toSeatStacks, bag);
    debitRemain(bag);
  }
  if (stacksTotal(remainStacks) > 0) {
    toSeatStacks = addStacks(toSeatStacks, remainStacks);
    remainStacks = emptyStacks();
  }

  const bannerSeats = call.bannerSeats.map((s) => {
    const got = addStacksByKey[s.key];
    if (!got || stacksTotal(got) <= 0) return s;
    const nextG = addStacks(
      normalizeStacks(s.stacksGarrisoned ?? stacksFromLegacy(s.garrisoned)),
      got,
    );
    let nextSent = normalizeStacks(s.stacksSent ?? stacksFromLegacy(s.sent));
    const sentPeel = peelFromStacks(nextSent, stacksTotal(got));
    nextSent = sentPeel.left;
    return syncBannerSeatScalars({
      ...s,
      stacksGarrisoned: nextG,
      stacksSent: nextSent,
      stacksInTransit: s.stacksInTransit,
    });
  });

  let nextArms: ArmsState = {
    garrison: cloneStacks(call.arms.garrison),
    host: hostLeftStacks,
  };
  if (stacksTotal(toSeatStacks) > 0) {
    nextArms = mergeStacksInto(nextArms, "garrison", toSeatStacks);
  }

  const parts: string[] = [];
  for (const s of [...call.bannerSeats].sort((a, b) =>
    a.key.localeCompare(b.key),
  )) {
    const got = addStacksByKey[s.key];
    const n = got ? stacksTotal(got) : 0;
    if (n) parts.push(`${s.name} +${n}`);
  }
  const seatN = stacksTotal(toSeatStacks);
  if (seatN) parts.push(`seat garrison +${seatN}`);
  const detail = parts.length ? parts.join(" · ") : "dispersed";

  return {
    ok: true,
    moved: host,
    call: {
      ...call,
      arms: nextArms,
      hostOrigin: originLeft,
      bannerSeats,
      lastToast: `Released ${host} host → bannermen (${detail}).`,
    },
    growth,
  };
}

/** Alias matching plan §E name. */
export const releaseHostToBannermen = releaseHostToBanners;

/** Derive loyalty bucket from favor (primary) + light material stress. */
export function loyaltyBucket(
  favor: number,
  opts: { food?: number; gold?: number } = {},
): LoyaltyBucket {
  let f = favor;
  if (opts.food !== undefined && opts.food <= 6) f -= 12;
  if (opts.gold !== undefined && opts.gold <= 5) f -= 8;
  if (f >= 68) return "true";
  if (f >= 38) return "strained";
  return "rebel-risk";
}

export function distanceBand(d: number): DistanceBand {
  if (d <= 5) return "near";
  if (d <= 9) return "mid";
  return "far";
}

export function distanceBetweenKeys(aKey: string, bKey: string): number {
  const a = parseKey(aKey);
  const b = parseKey(bKey);
  return hexDistance(a.q, a.r, b.q, b.r);
}

function seasonFactor(season: Season, band: DistanceBand): CallFactor {
  switch (season) {
    case "Summer":
      return {
        id: "season",
        label: "Campaign season",
        tone: "green",
        detail: "Long days favor travel and war.",
      };
    case "Autumn":
      return {
        id: "season",
        label: "Harvest roads",
        tone: band === "far" ? "yellow" : "green",
        detail:
          band === "far"
            ? "Far banners hesitate to leave the tithe unfinished."
            : "Roads hold; granaries fill.",
      };
    case "Spring":
      return {
        id: "season",
        label: "Thaw roads",
        tone: "yellow",
        detail: "Muddy tracks slow musters.",
      };
    case "Winter":
      return {
        id: "season",
        label: "Deep freeze",
        tone: band === "near" ? "yellow" : "red",
        detail:
          band === "near"
            ? "Home-range only — short rides still bite."
            : "Winter roads excuse the far banners.",
      };
  }
}

function loyaltyFactor(bucket: LoyaltyBucket): CallFactor {
  switch (bucket) {
    case "true":
      return {
        id: "loyalty",
        label: "True",
        tone: "green",
        detail: "Oath holds; they mean to ride.",
      };
    case "strained":
      return {
        id: "loyalty",
        label: "Strained",
        tone: "yellow",
        detail: "They will weigh cost against duty.",
      };
    case "rebel-risk":
      return {
        id: "loyalty",
        label: "Rebel risk",
        tone: "red",
        detail: "Cold favor — refusal or betrayal risk.",
      };
  }
}

function distanceFactor(band: DistanceBand, d: number): CallFactor {
  switch (band) {
    case "near":
      return {
        id: "distance",
        label: "Near",
        tone: "green",
        detail: `${d} hex — close enough to hear the horn.`,
      };
    case "mid":
      return {
        id: "distance",
        label: "Mid",
        tone: "yellow",
        detail: `${d} hex — a real march, not a stroll.`,
      };
    case "far":
      return {
        id: "distance",
        label: "Far",
        tone: "red",
        detail: `${d} hex — long roads, thin supply.`,
      };
  }
}

function prosperityFactor(loyalty: number, stocks?: { food: number; gold: number }): CallFactor {
  // Bannermen: loyalty stands in for local prosperity until full stocks exist.
  // Player answer: use seat food/gold.
  if (stocks) {
    const lean = stocks.food < 12 || stocks.gold < 10;
    const fat = stocks.food >= 24 && stocks.gold >= 30;
    if (fat) {
      return {
        id: "stores",
        label: "Stores fat",
        tone: "green",
        detail: `Food ${stocks.food} · gold ${stocks.gold} — can afford the march.`,
      };
    }
    if (lean) {
      return {
        id: "stores",
        label: "Stores lean",
        tone: stocks.food <= 6 ? "red" : "yellow",
        detail: `Food ${stocks.food} · gold ${stocks.gold} — march costs bite.`,
      };
    }
    return {
      id: "stores",
      label: "Stores fair",
      tone: "green",
      detail: `Food ${stocks.food} · gold ${stocks.gold}.`,
    };
  }
  if (loyalty >= 70) {
    return {
      id: "stores",
      label: "Prosperous hold",
      tone: "green",
      detail: "Yield and court look steady enough to field men.",
    };
  }
  if (loyalty >= 45) {
    return {
      id: "stores",
      label: "Modest hold",
      tone: "yellow",
      detail: "They can spare some spears, not a full host.",
    };
  }
  return {
    id: "stores",
    label: "Thin hold",
    tone: "red",
    detail: "Hard times — few mouths left to spare for war.",
  };
}

/** Spy tag stub (P5). Always neutral green note for P3 transparency. */
function spyFactorStub(): CallFactor {
  return {
    id: "spy",
    label: "Spy unread",
    tone: "green",
    detail: "No Proof chain yet — call on open loyalty only (P5 later).",
  };
}

function tonePenalty(t: CallFactor["tone"]): number {
  if (t === "green") return 0;
  if (t === "yellow") return 1;
  return 2;
}

/**
 * Will-they-show assessment: loyalty + distance + season + prosperity (+ spy stub).
 * Always names a human reason — silent random is unfair; winter roads are fair.
 */
export function assessShow(input: {
  favor: number;
  distance: number;
  season: Season;
  stocks?: { food: number; gold: number };
  /** 0–1 roll; omit for pure threshold (tests). */
  roll?: number;
}): ShowAssessment {
  const band = distanceBand(input.distance);
  const bucket = loyaltyBucket(input.favor, input.stocks);
  const factors: CallFactor[] = [
    loyaltyFactor(bucket),
    distanceFactor(band, input.distance),
    seasonFactor(input.season, band),
    prosperityFactor(input.favor, input.stocks),
    spyFactorStub(),
  ];

  // Base score from favor
  let score = input.favor / 100;
  // Distance
  if (band === "mid") score -= 0.12;
  if (band === "far") score -= 0.28;
  // Season
  if (input.season === "Summer") score += 0.1;
  if (input.season === "Spring") score -= 0.08;
  if (input.season === "Winter") score -= band === "near" ? 0.1 : 0.22;
  // Stores / prosperity
  if (input.stocks) {
    if (input.stocks.food <= 6) score -= 0.18;
    else if (input.stocks.food < 12) score -= 0.08;
    if (input.stocks.gold <= 5) score -= 0.1;
  } else if (input.favor < 45) {
    score -= 0.1;
  }

  score = Math.max(0, Math.min(1, score));

  const yellowRed = factors.reduce((n, f) => n + tonePenalty(f.tone), 0);
  // Paper shortcut: all green → join; one yellow → delay/partial; two+ → partial/refuse
  // Blend score + flag count; optional roll nudges borderline cases.
  const roll = input.roll ?? 0.5;
  const nudged = score + (roll - 0.5) * 0.15;

  let outcome: CallOutcome;
  if (bucket === "rebel-risk" && band === "far" && input.season === "Winter") {
    outcome = "refuse";
  } else if (nudged >= 0.72 && yellowRed <= 1) {
    outcome = "join";
  } else if (nudged >= 0.55 && yellowRed <= 3) {
    outcome = yellowRed >= 2 ? "partial" : "delay";
  } else if (nudged >= 0.38) {
    outcome = yellowRed >= 4 ? "refuse" : "partial";
  } else {
    outcome = "refuse";
  }

  const reason = nameReason(outcome, factors, input.season, band, bucket);
  return {
    loyalty: bucket,
    distance: input.distance,
    distanceBand: band,
    factors,
    showScore: Math.round(nudged * 100) / 100,
    outcome,
    reason,
  };
}

function nameReason(
  outcome: CallOutcome,
  factors: CallFactor[],
  season: Season,
  band: DistanceBand,
  bucket: LoyaltyBucket,
): string {
  const worst = [...factors].sort((a, b) => tonePenalty(b.tone) - tonePenalty(a.tone))[0];
  const lead = worst && worst.tone !== "green" ? worst.detail : factors[0]!.detail;

  switch (outcome) {
    case "join":
      return bucket === "true"
        ? `They ride — ${LOYALTY_LABELS[bucket].toLowerCase()} banners answer the horn.`
        : `They join despite doubts — ${lead}`;
    case "delay":
      return season === "Spring"
        ? "Thaw roads delay the muster — they will come late."
        : `They delay — ${lead}`;
    case "partial":
      return band === "far" || band === "mid"
        ? `Partial levy only — ${lead}`
        : `They send a thin company — ${lead}`;
    case "refuse":
      if (season === "Winter" && band !== "near") {
        return "Refuse — winter roads; far holds keep the hearth.";
      }
      if (bucket === "rebel-risk") {
        return `Refuse — ${LOYALTY_LABELS[bucket].toLowerCase()}; ${lead}`;
      }
      return `Refuse — ${lead}`;
  }
}

/** Light supply drain for a field host (Gold/Food tease). */
export function supplyCostForHost(hostSize: number, season: Season): { food: number; gold: number } {
  if (hostSize <= 0) return { food: 0, gold: 0 };
  const bands = Math.max(1, Math.ceil(hostSize / 20));
  let food = 2 + bands; // 3+ 
  let gold = 1 + Math.floor(bands / 2);
  if (season === "Winter") {
    food += 2;
    gold += 1;
  } else if (season === "Summer") {
    gold += 1; // campaign pay
  }
  return { food, gold };
}

function troopsForOutcome(
  outcome: CallOutcome,
  pool: number,
): number {
  switch (outcome) {
    case "join":
      return Math.max(4, Math.round(pool * 0.85));
    case "delay":
      // Late — still most of the levy, counted when they arrive (same turn for P3 simplicity)
      return Math.max(3, Math.round(pool * 0.7));
    case "partial":
      return Math.max(2, Math.round(pool * 0.35));
    case "refuse":
      return 0;
  }
}

function clampStock(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n));
}

function bannerLevyPool(b: BannermanCard): number {
  // Thin card → levy size from loyalty band
  return 8 + Math.floor(b.loyalty / 5); // ~19–28
}

export interface CallBannersInput {
  call: CallState;
  ledger: LedgerState;
  seatKey: string;
  season: Season;
  /** Current clock turn — used to enforce once-per-turn banner calls. */
  turn: number;
  /** Seed fragment for deterministic rolls. */
  rngSeed: string;
  /** Optional map for terrain-aware battle preview (P4 Slice 4). */
  map?: HexMap;
}

/** True when the player already sounded the vassal horn this turn. */
export function alreadyCalledBannersThisTurn(call: CallState, turn: number): boolean {
  return call.lastBannerCallTurn === turn && turn >= 0;
}

function ensureBannerSeats(
  call: CallState,
  bannermen: { key: string; name: string }[],
): BannerSeatArms[] {
  if (call.bannerSeats.length > 0) {
    // Keep existing seats; add any new bannermen missing from state
    const byKey = new Map(call.bannerSeats.map((s) => [s.key, s]));
    return bannermen.map((b) => {
      const existing = byKey.get(b.key);
      if (existing) return { ...existing, name: b.name };
      return normalizeBannerSeat({
        key: b.key,
        name: b.name,
        garrisoned: BANNER_BASE_GARRISON,
        sent: 0,
        inTransit: 0,
      });
    });
  }
  return createBannerSeats(bannermen);
}

/**
 * End-turn tick: multi-turn delayed banner replies → host; pending seat
 * levy/dismiss finalizes; open liege delay clocks tick down; horn inbox notes.
 * Pass growth when a pending levy may resolve (main always does).
 * Optional `turn` stamps arrival horn entries (defaults 0).
 */
export function tickCallOnEndTurn(
  call: CallState,
  growth?: import("./growth.ts").GrowthState,
  opts?: { turn?: number; map?: HexMap; season?: Season },
): {
  call: CallState;
  growth: import("./growth.ts").GrowthState | undefined;
  arrived: number;
  levyResolved: number;
  log: string[];
} {
  const log: string[] = [];
  let arrived = 0;
  let arrivedStacks = emptyStacks();
  let hostOrigin = cloneHostOrigin(call.hostOrigin ?? emptyHostOrigin());
  const turn = opts?.turn ?? 0;
  const seatsByKey = new Map(
    call.bannerSeats.map((s) => [s.key, normalizeBannerSeat(s)]),
  );
  const stillPending: PendingBannerReply[] = [];
  let hornInbox = call.hornInbox ?? createHornInbox();

  for (const p of call.pendingBannerReplies ?? []) {
    const nextLeft = p.turnsLeft - 1;
    const seat = normalizeBannerSeat(
      seatsByKey.get(p.key) ?? {
        key: p.key,
        name: p.name,
        garrisoned: 0,
        sent: 0,
        inTransit: 0,
      },
    );
    if (nextLeft <= 0) {
      const moveStacks = normalizeStacks(
        p.stacksCommitted ?? stacksFromLegacy(Math.max(0, p.committed)),
      );
      const move = stacksTotal(moveStacks);
      arrived += move;
      arrivedStacks = addStacks(arrivedStacks, moveStacks);
      hostOrigin = creditHostOrigin(hostOrigin, p.key, moveStacks);
      // Peel transit stacks; credit sent stacks
      const transit = normalizeStacks(seat.stacksInTransit ?? emptyStacks());
      const peeled = peelFromStacks(transit, move);
      seat.stacksInTransit = peeled.left;
      seat.stacksSent = addStacks(
        normalizeStacks(seat.stacksSent ?? emptyStacks()),
        moveStacks,
      );
      seatsByKey.set(p.key, syncBannerSeatScalars(seat));
      log.push(
        `${p.name}: ${move} spears arrive at your seat (were en route · ${p.seasonAtCall}).`,
      );
      if (p.hornId) {
        hornInbox = updateHornEntry(hornInbox, p.hornId, {
          kind: "arrival",
          pending: false,
          turnsLeft: 0,
          body: `${p.name}: ${move} spears arrived from the delayed column (${p.seasonAtCall} roads).`,
          read: false,
        });
      }
      const posted = postHorn(hornInbox, {
        kind: "arrival",
        title: `${p.name} column arrived`,
        body: `${move} spears join your host from the delayed march.`,
        turn,
        season: p.seasonAtCall,
        actorName: p.name,
        actorKey: p.key,
      });
      hornInbox = posted.inbox;
    } else {
      stillPending.push({ ...p, turnsLeft: nextLeft });
      seatsByKey.set(p.key, seat);
      log.push(
        `${p.name}: still en route — ${nextLeft} turn${nextLeft === 1 ? "" : "s"} left (${p.committed} spears).`,
      );
      if (p.hornId) {
        hornInbox = updateHornEntry(hornInbox, p.hornId, {
          turnsLeft: nextLeft,
          pending: true,
          body: `${p.name} delayed — ${p.committed} spears, ${nextLeft} turn${nextLeft === 1 ? "" : "s"} left (${p.seasonAtCall}).`,
        });
      }
    }
  }

  // Legacy seats with inTransit but no pending row (pre-Slice-2 saves): dump next End turn
  for (const s0 of seatsByKey.values()) {
    const s = normalizeBannerSeat(s0);
    const covered = stillPending
      .filter((p) => p.key === s.key)
      .reduce((n, p) => n + p.committed, 0);
    const orphan = Math.max(0, s.inTransit - covered);
    if (orphan > 0) {
      // Prefer typed transit remainder after covered scalar reservation
      const transit = normalizeStacks(s.stacksInTransit ?? emptyStacks());
      const peeled = peelFromStacks(transit, orphan);
      const orphanStacks = peeled.takenStacks;
      arrived += orphan;
      arrivedStacks = addStacks(arrivedStacks, orphanStacks);
      hostOrigin = creditHostOrigin(hostOrigin, s.key, orphanStacks);
      s.stacksInTransit = peeled.left;
      // If covered still pending, keep those heads in transit as legacy MAA
      if (covered > 0 && stacksTotal(s.stacksInTransit) < covered) {
        s.stacksInTransit = stacksFromLegacy(covered);
      }
      s.stacksSent = addStacks(
        normalizeStacks(s.stacksSent ?? emptyStacks()),
        orphanStacks,
      );
      seatsByKey.set(s.key, syncBannerSeatScalars(s));
      log.push(
        `${s.name}: ${orphan} spears arrive at your seat (were en route).`,
      );
    }
  }
  const seats = [...seatsByKey.values()].map((s) => syncBannerSeatScalars(s));

  let liegeDelayTurnsLeft = call.liegeDelayTurnsLeft;
  let liegeCallPending = call.liegeCallPending;
  let lastToast = call.lastToast;
  let favor = call.liege.favor;

  if (liegeDelayTurnsLeft !== null && liegeCallPending) {
    const next = liegeDelayTurnsLeft - 1;
    if (next <= 0) {
      // Delay expired without a real answer → treat as soft refuse
      liegeDelayTurnsLeft = null;
      liegeCallPending = false;
      favor = Math.max(0, favor - REFUSE_FAVOR_HIT);
      lastToast = `${call.liege.name}'s delay ran out — favor −${REFUSE_FAVOR_HIT} (as refuse).`;
      log.push(lastToast);
      const expired = postHorn(hornInbox, {
        kind: "system",
        title: "Liege delay expired",
        body: lastToast,
        turn,
        actorName: call.liege.name,
      });
      hornInbox = expired.inbox;
    } else {
      liegeDelayTurnsLeft = next;
      log.push(
        `Liege delay: ${next} turn${next === 1 ? "" : "s"} left to answer ${call.liege.name}.`,
      );
    }
  }

  let arms =
    arrived > 0
      ? {
          ...normalizeArms(call.arms),
          host: addStacks(normalizeArms(call.arms).host, arrivedStacks),
        }
      : normalizeArms(call.arms);
  let pendingLevy = call.pendingLevy;
  let nextGrowth = growth;
  let levyResolved = 0;

  if (pendingLevy && pendingLevy.amount > 0) {
    const amt = pendingLevy.amount;
    if (pendingLevy.kind === "conscript") {
      arms = addMaa(arms, "garrison", amt);
      levyResolved = amt;
      const msg = `Levy arrived: ${amt} joined garrison (${garrisonTotal(arms)} standing).`;
      log.push(msg);
      lastToast = msg;
    } else if (pendingLevy.kind === "discharge" && nextGrowth) {
      const able = Math.max(0, Math.floor(nextGrowth.seat.ableBodied) + amt);
      const gen = Math.max(0, Math.floor(nextGrowth.seat.genPop));
      nextGrowth = {
        ...nextGrowth,
        seat: {
          ...nextGrowth.seat,
          ableBodied: able,
          genPop: gen,
          population: gen + able,
        },
      };
      levyResolved = amt;
      const msg = `Dismiss arrived: ${amt} returned to able (${able} able free · ${garrisonTotal(arms)} garrison).`;
      log.push(msg);
      lastToast = msg;
    } else if (pendingLevy.kind === "discharge" && !nextGrowth) {
      // No growth ctx — leave pending for a caller that supplies it
      log.push(`Dismiss of ${amt} still pending (no growth context).`);
    }
    if (!(pendingLevy.kind === "discharge" && !nextGrowth)) {
      pendingLevy = null;
    }
  }

  if (arrived > 0 && !lastToast) {
    lastToast = `${arrived} spears arrived from delayed columns.`;
  }

  // P4 Slice 3: advance open liege quest one march tick when map+season provided
  let activeQuest = call.activeQuest ?? null;
  let questFavorDelta = 0;
  if (activeQuest && opts?.map && opts?.season && isQuestOpen(activeQuest)) {
    const qt = tickLiegeQuest({
      quest: activeQuest,
      map: opts.map,
      season: opts.season,
      turn,
    });
    activeQuest = qt.quest;
    if (qt.log.length) log.push(...qt.log);
    if (qt.arrived && activeQuest.status === "arrived") {
      // Prior phase before this tick: returning → seat fold into host.
      const wasReturning = call.activeQuest?.status === "returning";

      if (wasReturning) {
        // Home leg done: fold discrete stacks + ownership into HOST.
        const foldedHome = foldArmyIntoHost(arms, hostOrigin, activeQuest.army);
        arms = foldedHome.arms;
        hostOrigin = foldedHome.hostOrigin;
        const homeSpears = foldedHome.folded;
        activeQuest = completeLiegeQuest(
          {
            ...activeQuest,
            army: syncArmyComposition(
              { ...activeQuest.army, status: "idle", path: [], pathIndex: 0 },
              { stacks: emptyStacks(), hostOrigin: emptyHostOrigin(), strength: 0 },
            ),
          },
          `${activeQuest.army.label} home — ${homeSpears} join host.`,
        );
        const msg = `Column home: +${homeSpears} host (now ${hostTotal(arms)}). Release host to return spears to banners.`;
        log.push(msg);
        lastToast = lastToast ?? msg;
        const posted = postHorn(hornInbox, {
          kind: "arrival",
          title: `Column home: ${activeQuest.fromKey}`,
          body: msg,
          turn,
          season: opts.season,
          actorName: call.liege.name,
          pending: false,
        });
        hornInbox = posted.inbox;
      } else {
        // Field arrival: resolve battle, then begin return march (survivors stay on token / host path).
        let battleLine = "";
        let survivors = Math.max(0, Math.floor(activeQuest.army.strength));
        if (opts.map && survivors > 0) {
          const biome = biomeAt(opts.map, activeQuest.objectiveKey);
          const attacker = compositionFromHost({
            total: survivors,
            label: activeQuest.army.label || `${call.liege.name}'s host`,
            readiness: 0.9,
          });
          const foeN = Math.max(4, Math.round(survivors * 0.95));
          const defender = compositionFromHost({
            total: foeN,
            label: `Foes at ${activeQuest.objectiveLabel}`,
            favorites: ["lightHorse", "archer", "maa"],
            readiness: 0.85,
            generalBonus: 0.05,
          });
          const br = resolveBattle({
            attacker,
            defender,
            biome,
            season: opts.season,
            preparedField: true,
          });
          const lost = applyLosses(survivors, br.attackerLossFrac);
          survivors = lost.remaining;
          activeQuest = applyQuestSurvivorCount(activeQuest, survivors);
          battleLine =
            ` ${br.summary}` +
            (lost.lost > 0
              ? ` Host loses ${lost.lost}; ${survivors} begin the road home.`
              : ` Host holds; ${survivors} begin the road home.`);
        }
        questFavorDelta = JOIN_FAVOR_GAIN;
        favor = Math.min(100, favor + questFavorDelta);

        if (opts.map && survivors > 0) {
          const ret = beginQuestReturn(activeQuest, opts.map, opts.season, turn);
          if (ret.ok) {
            activeQuest = ret.quest;
            // Immediate home (same hex) — fold now
            if (activeQuest.status === "arrived") {
              const foldedImm = foldArmyIntoHost(arms, hostOrigin, activeQuest.army);
              arms = foldedImm.arms;
              hostOrigin = foldedImm.hostOrigin;
              survivors = foldedImm.folded;
              activeQuest = completeLiegeQuest(
                {
                  ...activeQuest,
                  army: syncArmyComposition(
                    { ...activeQuest.army, status: "idle", path: [], pathIndex: 0 },
                    { stacks: emptyStacks(), hostOrigin: emptyHostOrigin(), strength: 0 },
                  ),
                },
                `${activeQuest.army.label} home — ${survivors} join host.`,
              );
              battleLine += ` Already at seat: +${survivors} host.`;
            } else {
              battleLine += ` Returning to seat.`;
            }
          } else {
            // No path home — fold discrete column into host immediately.
            const foldedRet = foldArmyIntoHost(arms, hostOrigin, activeQuest.army);
            arms = foldedRet.arms;
            hostOrigin = foldedRet.hostOrigin;
            survivors = foldedRet.folded;
            activeQuest = completeLiegeQuest(
              {
                ...activeQuest,
                army: syncArmyComposition(
                  { ...activeQuest.army, status: "idle", path: [], pathIndex: 0 },
                  { stacks: emptyStacks(), hostOrigin: emptyHostOrigin(), strength: 0 },
                ),
              },
              `Field done; return path failed (${ret.reason}) — ${survivors} join host at seat.`,
            );
            battleLine += ` Return failed (${ret.reason}); +${survivors} host.`;
          }
        } else if (survivors > 0) {
          // No map: fold discrete column into host — never strand spears on a closed quest.
          const foldedMap = foldArmyIntoHost(arms, hostOrigin, activeQuest.army);
          arms = foldedMap.arms;
          hostOrigin = foldedMap.hostOrigin;
          survivors = foldedMap.folded;
          activeQuest = completeLiegeQuest(
            {
              ...activeQuest,
              army: syncArmyComposition(
                { ...activeQuest.army, status: "idle", path: [], pathIndex: 0 },
                { stacks: emptyStacks(), hostOrigin: emptyHostOrigin(), strength: 0 },
              ),
            },
            `${activeQuest.army.label} — ${survivors} join host at seat (no return march).`,
          );
          battleLine += ` +${survivors} host.`;
        } else {
          activeQuest = completeLiegeQuest(
            {
              ...activeQuest,
              army: {
                ...activeQuest.army,
                strength: 0,
                status: "idle",
                path: [],
                pathIndex: 0,
              },
            },
            `${activeQuest.army.label} broken at ${activeQuest.objectiveLabel} — no survivors return.`,
          );
          battleLine += ` No survivors to return.`;
        }

        const msg =
          `Field resolved: ${describeQuest(activeQuest)} · favor +${questFavorDelta}.` +
          battleLine;
        log.push(msg);
        lastToast = lastToast ?? msg;
        const posted = postHorn(hornInbox, {
          kind: "arrival",
          title: `Host arrives: ${activeQuest.objectiveLabel}`,
          body: msg,
          turn,
          season: opts.season,
          actorName: call.liege.name,
          pending: false,
        });
        hornInbox = posted.inbox;
      }
    } else if (qt.failed || activeQuest.status === "failed") {
      // Deadline / stuck / late — fold remaining column into host (do not vanish spears).
      questFavorDelta = -REFUSE_FAVOR_HIT;
      favor = Math.max(0, favor + questFavorDelta);
      const foldedFail = foldArmyIntoHost(arms, hostOrigin, activeQuest.army);
      arms = foldedFail.arms;
      hostOrigin = foldedFail.hostOrigin;
      const rescued = foldedFail.folded;
      activeQuest = {
        ...activeQuest,
        status: "failed",
        army: {
          ...syncArmyComposition(
            { ...activeQuest.army, status: "idle", path: [], pathIndex: 0 },
            { stacks: emptyStacks(), hostOrigin: emptyHostOrigin(), strength: 0 },
          ),
          note:
            rescued > 0
              ? "Column folded after failed quest."
              : "Column lost — nothing to fold.",
        },
        note:
          activeQuest.note ||
          (rescued > 0
            ? `Quest failed — ${rescued} spears limp home to host.`
            : "Quest failed — no survivors."),
      };
      const failNote = activeQuest.note || describeQuest(activeQuest);
      const msg =
        rescued > 0
          ? `Quest failed: ${failNote} · +${rescued} host (now ${hostTotal(arms)}). Release host to return spears to banners. · favor ${questFavorDelta}.`
          : `Quest failed: ${failNote} · favor ${questFavorDelta}.`;
      log.push(msg);
      lastToast = lastToast ?? msg;
      const posted = postHorn(hornInbox, {
        kind: "system",
        title: `Quest failed: ${activeQuest.objectiveLabel}`,
        body: msg,
        turn,
        season: opts.season,
        actorName: call.liege.name,
        pending: false,
      });
      hornInbox = posted.inbox;
    } else if (qt.stuck) {
      log.push(activeQuest.note || `${activeQuest.army.label} is stuck on the road.`);
    }
  }

  return {
    call: {
      ...call,
      liege: favor !== call.liege.favor ? { ...call.liege, favor } : call.liege,
      arms,
      hostOrigin,
      bannerSeats: seats,
      pendingLevy,
      liegeDelayTurnsLeft,
      liegeCallPending,
      pendingBannerReplies: stillPending,
      hornInbox,
      activeQuest,
      lastToast,
      lastCallLog:
        log.length > 0 ? [...log, ...call.lastCallLog].slice(0, 24) : call.lastCallLog,
    },
    growth: nextGrowth,
    arrived,
    levyResolved,
    log,
  };
}

/** You call your bannermen — will they show? Once per turn. */
export function callBannermen(input: CallBannersInput): MusterResult {
  const emptySupply = { food: 0, gold: 0 };
  if (alreadyCalledBannersThisTurn(input.call, input.turn)) {
    return {
      direction: "you-to-banners",
      answers: input.call.lastAnswers,
      arms: { ...input.call.arms },
      stocks: { ...input.ledger.stocks },
      supply: emptySupply,
      log: [
        `Already called your banners this turn (turn ${input.turn}). End turn to sound the horn again.`,
      ],
      preview: input.call.lastPreview ?? "Call blocked — once per turn.",
      toast: "Already called this turn — end turn first.",
      blocked: true,
    };
  }

  const rng = createRng(`${input.rngSeed}|call|${input.call.callCount}`);
  const answers: BannerAnswer[] = [];
  let hostGainNow = 0;
  let transitGain = 0;
  let hostStacksGainNow = emptyStacks();
  let hostOriginAfter = cloneHostOrigin(
    input.call.hostOrigin ?? emptyHostOrigin(),
  );
  const log: string[] = [];
  const seats = ensureBannerSeats(input.call, input.ledger.bannermen);
  const seatByKey = new Map(seats.map((s) => [s.key, { ...s }]));
  const newPending: PendingBannerReply[] = [];
  let hornInbox = input.call.hornInbox ?? createHornInbox();

  {
    const youCall = postHorn(hornInbox, {
      kind: "banner-call",
      title: "You sound the horn",
      body: `Turn ${input.turn} · ${input.season} — calling ${input.ledger.bannermen.length} banner${input.ledger.bannermen.length === 1 ? "" : "s"}.`,
      turn: input.turn,
      season: input.season,
    });
    hornInbox = youCall.inbox;
  }

  for (const b of input.ledger.bannermen) {
    const d = distanceBetweenKeys(input.seatKey, b.key);
    const roll = rng();
    const a = assessShow({
      favor: b.loyalty,
      distance: d,
      season: input.season,
      roll,
    });
    const seat = normalizeBannerSeat(
      seatByKey.get(b.key) ?? {
        key: b.key,
        name: b.name,
        garrisoned: BANNER_BASE_GARRISON,
        sent: 0,
        inTransit: 0,
      },
    );
    const poolStacks = normalizeStacks(
      seat.stacksGarrisoned ?? stacksFromLegacy(Math.max(seat.garrisoned, 0)),
    );
    const pool = stacksTotal(poolStacks);
    // How many they commit from their garrisoned pool (typed peel)
    const want = Math.min(pool, troopsForOutcome(a.outcome, Math.max(pool, bannerLevyPool(b))));
    const peel = peelFromStacks(poolStacks, want);
    const committedStacks = peel.takenStacks;
    const committed = peel.taken;
    let troopsSent = 0;
    let troopsInTransit = 0;
    let stacksSentNow = emptyStacks();
    let stacksInTransitNow = emptyStacks();
    let delayTurnsLeft: number | undefined;
    if (a.outcome === "join" || a.outcome === "partial") {
      // Near/mid arrive this muster; far start en route (seasonal multi-turn)
      if (a.distanceBand === "far") {
        troopsInTransit = committed;
        stacksInTransitNow = committedStacks;
        transitGain += committed;
        delayTurnsLeft = bannerDelayTurns(input.season, "far");
      } else {
        troopsSent = committed;
        stacksSentNow = committedStacks;
        hostGainNow += committed;
        hostStacksGainNow = addStacks(hostStacksGainNow, committedStacks);
        hostOriginAfter = creditHostOrigin(
          hostOriginAfter,
          b.key,
          committedStacks,
        );
      }
    } else if (a.outcome === "delay") {
      // Delay = march late — seasonal multi-turn en route
      troopsInTransit = committed;
      stacksInTransitNow = committedStacks;
      transitGain += committed;
      delayTurnsLeft = bannerDelayTurns(input.season, a.distanceBand);
    }
    // refuse → 0

    seat.stacksGarrisoned = peel.left;
    seat.stacksSent = addStacks(
      normalizeStacks(seat.stacksSent ?? emptyStacks()),
      stacksSentNow,
    );
    seat.stacksInTransit = addStacks(
      normalizeStacks(seat.stacksInTransit ?? emptyStacks()),
      stacksInTransitNow,
    );
    seatByKey.set(b.key, syncBannerSeatScalars(seat));

    if (troopsInTransit > 0 && delayTurnsLeft != null) {
      const delayPost = postHorn(hornInbox, {
        kind: "banner-delay",
        title: `${b.name} delays`,
        body: `${b.name}: ${troopsInTransit} spears en route — ${delayTurnsLeft} turn${delayTurnsLeft === 1 ? "" : "s"} (${input.season}, ${a.distanceBand}). ${a.reason}`,
        turn: input.turn,
        season: input.season,
        actorKey: b.key,
        actorName: b.name,
        outcome: a.outcome,
        pending: true,
        turnsLeft: delayTurnsLeft,
      });
      hornInbox = delayPost.inbox;
      newPending.push({
        key: b.key,
        name: b.name,
        turnsLeft: delayTurnsLeft,
        committed: troopsInTransit,
        stacksCommitted: cloneStacks(stacksInTransitNow),
        reason: a.reason,
        seasonAtCall: input.season,
        distanceBand: a.distanceBand,
        postedTurn: input.turn,
        hornId: delayPost.entry.id,
      });
    } else {
      const replyPost = postHorn(hornInbox, {
        kind: "banner-reply",
        title: `${b.name}: ${OUTCOME_LABELS[a.outcome]}`,
        body: `${b.name}: ${OUTCOME_LABELS[a.outcome]} (+${troopsSent}) — ${a.reason}`,
        turn: input.turn,
        season: input.season,
        actorKey: b.key,
        actorName: b.name,
        outcome: a.outcome,
      });
      hornInbox = replyPost.inbox;
    }

    answers.push({
      key: b.key,
      name: b.name,
      outcome: a.outcome,
      reason: a.reason,
      troopsSent,
      troopsInTransit,
      delayTurnsLeft,
      distance: d,
      distanceBand: a.distanceBand,
      loyalty: a.loyalty,
      showScore: a.showScore,
      factors: a.factors,
    });
    const moveNote =
      troopsInTransit > 0
        ? `+${troopsSent} arrived · +${troopsInTransit} en route (${delayTurnsLeft ?? "?"}t)`
        : `+${troopsSent}`;
    log.push(
      `${b.name}: ${OUTCOME_LABELS[a.outcome]} (${moveNote}) — ${a.reason}`,
    );
  }

  // Phase 4.5 §2.0: Garrison stays at your town. Host = banner visitors only.
  // Call must NOT peel seat garrison → host (killed 0.4.1 auto seat column).
  // Typed stacks + hostOrigin credit (5b2) — no scalar addMaa collapse.
  let armsAfterMuster: ArmsState = {
    garrison: cloneStacks(input.call.arms.garrison),
    host: cloneStacks(input.call.arms.host),
  };
  if (hostGainNow > 0) {
    armsAfterMuster = {
      ...armsAfterMuster,
      host: addStacks(armsAfterMuster.host, hostStacksGainNow),
    };
  }
  const garrisonLeft = garrisonTotal(armsAfterMuster);
  const hostAfter = hostTotal(armsAfterMuster);
  log.unshift(
    `Garrison holds the seat (${garrisonLeft}). Host is banner visitors only (${hostAfter}` +
      (transitGain > 0 ? ` · +${transitGain} en route` : "") +
      ").",
  );
  if (transitGain > 0) {
    const maxT = Math.max(0, ...newPending.map((p) => p.turnsLeft), 1);
    log.push(
      `${transitGain} spears still en route to your seat (up to ${maxT} End turn${maxT === 1 ? "" : "s"}, ${input.season} roads).`,
    );
  }

  const supply = supplyCostForHost(hostAfter + transitGain, input.season);
  const stocks: StockMap = { ...input.ledger.stocks };
  stocks.food = clampStock(stocks.food - supply.food);
  stocks.gold = clampStock(stocks.gold - supply.gold);
  log.push(
    `Supply tease: −${supply.food} food · −${supply.gold} gold (host ${hostAfter}, en route ${transitGain}, ${input.season}).`,
  );

  const joined = answers.filter((x) => x.outcome === "join").length;
  const refused = answers.filter((x) => x.outcome === "refuse").length;
  const toast =
    answers.length === 0
      ? "No bannermen to call."
      : refused === answers.length
        ? "The horn echoes unanswered."
        : joined === answers.length && transitGain === 0
          ? "Every banner rides."
          : `Call answered: ${joined} join · ${refused} refuse · host ${hostAfter}` +
            (transitGain > 0 ? ` · ${transitGain} en route` : "") +
            ".";

  // No battle preview on banner muster — field resolve only after liege quest arrival.
  const preview =
    hostAfter + transitGain > 0
      ? `Muster: host ${hostAfter}` + (transitGain ? ` · +${transitGain} still on the road` : "") + "."
      : "Muster: empty host — no column formed.";
  log.push(preview);

  const bannerSeats = input.ledger.bannermen.map(
    (b) => seatByKey.get(b.key) ?? createBannerSeats([b])[0]!,
  );

  return {
    direction: "you-to-banners",
    answers,
    arms: armsAfterMuster,
    hostOrigin: hostOriginAfter,
    bannerSeats,
    bannerSeatsReplace: true,
    pendingBannerReplies: newPending,
    pendingBannerRepliesReplace: false,
    hornInbox,
    stocks,
    supply,
    log,
    preview,
    toast,
    /** Stamped onto call via applyMuster when not blocked. */
    blocked: false,
  };
}

export interface AnswerLiegeInput {
  call: CallState;
  ledger: LedgerState;
  /** Forced outcome for UI choice; if omit, assess from favor/season. */
  choice?: CallOutcome;
  /**
   * Spears to send when choice is partial (UI modal).
   * Clamped to [1, garrison-1] when garrison > 1; ignored otherwise.
   */
  troopsSentOverride?: number;
  /** Turns granted on delay (default DEFAULT_DELAY_TURNS). */
  delayTurns?: number;
  season: Season;
  seatKey: string;
  /** Liege seat assumed at map origin for distance flavor when needed. */
  liegeKey?: string;
  rngSeed: string;
  /** Current turn (horn inbox stamp). */
  turn?: number;
  /** When set with join/partial, starts a liege quest on the hex map. */
  map?: HexMap;
  /** Override objective hex; default = pickDefaultObjective from seat. */
  objectiveKey?: string;
  objectiveLabel?: string;
}

/**
 * How many spears march on a liege answer.
 * `available` = host + garrison (field host counts; peel order is host-first in answerLiegeCall).
 * join = all available; refuse/delay = 0; partial = override or ~half (keeps ≥1 when pool ≥ 2).
 */
export function resolveLiegeTroopsSent(
  outcome: CallOutcome,
  available: number,
  troopsSentOverride?: number,
): number {
  const pool = Math.max(0, available);
  switch (outcome) {
    case "join":
      return pool;
    case "refuse":
    case "delay":
      return 0;
    case "partial": {
      if (pool <= 0) return 0;
      if (pool === 1) return 1; // can't keep a seat spear and still send
      if (
        troopsSentOverride !== undefined &&
        Number.isFinite(troopsSentOverride)
      ) {
        return Math.max(1, Math.min(pool - 1, Math.round(troopsSentOverride)));
      }
      return Math.max(1, Math.min(pool - 1, Math.round(pool * 0.5)));
    }
  }
}

/** Peel spears from host first, then garrison (immutable). */
export function peelArmsForLiegeSend(
  arms: ArmsState,
  troops: number,
): ArmsState {
  return peelArmsStacksForLiegeSend(normalizeArms(arms), troops);
}

/** Peel for liege send with ownership tracking (5b3). */
export function peelArmsForLiegeSendDetail(
  arms: ArmsState,
  origin: HostOriginMap | null | undefined,
  troops: number,
) {
  return peelArmsStacksForLiegeSendDetail(normalizeArms(arms), origin, troops);
}

/**
 * Per-turn / season-tick upkeep for spears already in your field host.
 * Same curve as march supply tease (`supplyCostForHost`) — extra food+gold when host > 0.
 */
export function applyHostUpkeep(
  ledger: LedgerState,
  hostSize: number,
  season: Season,
): {
  ledger: LedgerState;
  supply: { food: number; gold: number };
  log: string[];
} {
  const supply = supplyCostForHost(hostSize, season);
  if (supply.food <= 0 && supply.gold <= 0) {
    return { ledger, supply, log: [] };
  }
  const stocks: StockMap = { ...ledger.stocks };
  stocks.food = clampStock(stocks.food - supply.food);
  stocks.gold = clampStock(stocks.gold - supply.gold);
  const log = [
    `Host upkeep (${hostSize} spears): −${supply.food} food · −${supply.gold} gold.`,
  ];
  return {
    ledger: {
      ...ledger,
      stocks,
      lastTickLog: [...log, ...ledger.lastTickLog].slice(0, 16),
    },
    supply,
    log,
  };
}

/** Liege calls you — you answer (join · delay · partial · refuse). */
export function answerLiegeCall(input: AnswerLiegeInput): MusterResult {
  const rng = createRng(`${input.rngSeed}|answer|${input.call.callCount}`);
  const liegeKey = input.liegeKey ?? "0,0";
  // Distance from your seat to liege seat (map seat is yours; liege off-map → use seat-to-origin or 3)
  let d = 3;
  try {
    d = Math.max(2, distanceBetweenKeys(input.seatKey, liegeKey));
  } catch {
    d = 3;
  }
  // If liege key is your seat (same), treat as near court distance
  if (liegeKey === input.seatKey) d = 2;

  const assessed = assessShow({
    favor: input.call.liege.favor,
    distance: d,
    season: input.season,
    stocks: {
      food: input.ledger.stocks.food,
      gold: input.ledger.stocks.gold,
    },
    roll: rng(),
  });

  const outcome = input.choice ?? assessed.outcome;
  // Re-name reason if player picked a different political answer
  const reason =
    input.choice && input.choice !== assessed.outcome
      ? playerChoiceReason(input.choice, input.season, assessed)
      : assessed.reason;

  const garrisonBefore = garrisonTotal(input.call.arms);
  const hostBefore = hostTotal(input.call.arms);
  const available = Math.max(0, garrisonBefore) + Math.max(0, hostBefore);
  const troops = resolveLiegeTroopsSent(
    outcome,
    available,
    input.troopsSentOverride,
  );
  // March to liege peels field host first, then seat garrison (does not inflate your host)
  let armsAfter = normalizeArms(input.call.arms);
  let hostOriginAfter = cloneHostOrigin(input.call.hostOrigin ?? emptyHostOrigin());
  let sendStacks = emptyStacks();
  let sendOrigin = emptyHostOrigin();
  if (outcome !== "delay" && troops > 0) {
    const peelSend = peelArmsForLiegeSendDetail(
      input.call.arms,
      input.call.hostOrigin,
      troops,
    );
    armsAfter = peelSend.arms;
    hostOriginAfter = peelSend.origin;
    sendStacks = peelSend.takenStacks;
    sendOrigin = peelSend.takenByKey;
  }

  // Delay keeps the call open with a clock; other answers close it via applyMuster
  const delayTurnsLeft =
    outcome === "delay"
      ? (input.delayTurns ?? DEFAULT_DELAY_TURNS)
      : undefined;

  const supply =
    troops > 0
      ? supplyCostForHost(troops, input.season)
      : { food: 0, gold: 0 };
  const stocks: StockMap = { ...input.ledger.stocks };
  stocks.food = clampStock(stocks.food - supply.food);
  stocks.gold = clampStock(stocks.gold - supply.gold);

  // Favor shifts with how you answer (constants at top of file)
  let favor = input.call.liege.favor;
  if (outcome === "join") favor = Math.min(100, favor + JOIN_FAVOR_GAIN);
  else if (outcome === "delay") favor = Math.max(0, favor - DELAY_FAVOR_HIT);
  else if (outcome === "partial") favor = Math.max(0, favor - PARTIAL_FAVOR_HIT);
  else favor = Math.max(0, favor - REFUSE_FAVOR_HIT);

  const playerAnswer: PlayerAnswer = {
    outcome,
    reason,
    troopsSent: troops,
    delayTurnsLeft,
    loyalty: loyaltyBucket(favor, {
      food: stocks.food,
      gold: stocks.gold,
    }),
    showScore: assessed.showScore,
    factors: assessed.factors,
  };

  const log = [
    `Liege ${input.call.liege.name} called the banners.`,
    outcome === "delay"
      ? `You: ${OUTCOME_LABELS[outcome]} — ${delayTurnsLeft} turn${delayTurnsLeft === 1 ? "" : "s"} to give a real answer. ${reason}`
      : `You: ${OUTCOME_LABELS[outcome]} (${troops} spears) — ${reason}`,
    `Favor with liege now ${favor} (${LOYALTY_LABELS[playerAnswer.loyalty]}).`,
  ];
  if (troops > 0 || supply.food > 0 || supply.gold > 0) {
    log.push(
      `Supply tease: −${supply.food} food · −${supply.gold} gold.`,
    );
  }
  if (outcome !== "delay") {
    log.push(
      `Arms garr ${garrisonBefore}→${armsAfter.garrison} · host ${hostBefore}→${armsAfter.host} (host peeled first).`,
    );
  } else {
    log.push(
      `Arms stay garr ${garrisonBefore} · host ${hostBefore} while you delay (no spears marched yet).`,
    );
  }

  let preview: string;
  if (outcome === "refuse") {
    preview = "Answer: hearth kept — no column for the liege.";
  } else if (outcome === "delay") {
    preview = `Answer: delayed — real reply due within ${delayTurnsLeft} turn(s).`;
  } else if (troops > 0) {
    preview = `Answer: ${troops} spears peel for the liege column (march starts if a field is set).`;
  } else {
    preview = "Answer: no spears to send.";
  }
  log.push(preview);

  // P4 Slice 3: join/partial with map → start liege quest (host token marches)
  let startedQuest: LiegeQuest | null | undefined = undefined;
  if (
    input.map &&
    troops > 0 &&
    (outcome === "join" || outcome === "partial") &&
    !isQuestOpen(input.call.activeQuest)
  ) {
    const obj =
      input.objectiveKey != null
        ? {
            key: input.objectiveKey,
            label:
              input.objectiveLabel ??
              input.map.cells.get(input.objectiveKey)?.name ??
              input.objectiveKey,
          }
        : pickDefaultObjective(input.map, input.seatKey);
    if (obj) {
      const started = startLiegeQuest({
        liegeName: input.call.liege.name,
        map: input.map,
        fromKey: input.seatKey,
        objectiveKey: obj.key,
        objectiveLabel: obj.label,
        season: input.season,
        turn: input.turn ?? 0,
        strength: troops,
        stacks: sendStacks,
        hostOrigin: sendOrigin,
        armyLabel: `Host for ${input.call.liege.name}`,
      });
      if (started.ok) {
        startedQuest = started.quest;
        log.push(
          `Quest: march to ${started.quest.objectiveLabel} in ≤${started.quest.deadlineTurn - (input.turn ?? 0)} turns (${Number(started.path.totalCost).toFixed(1)} path cost).`,
        );
      } else {
        log.push(`Quest not started: ${started.reason}`);
      }
    } else {
      log.push("Quest not started: no objective hex on map.");
    }
  }

  const toast =
    outcome === "delay"
      ? `Delayed liege call — ${delayTurnsLeft} turns left. Favor −${DELAY_FAVOR_HIT}.`
      : startedQuest
        ? `Answered liege: ${OUTCOME_LABELS[outcome]} — host marches on ${startedQuest.objectiveLabel}.`
        : `Answered liege: ${OUTCOME_LABELS[outcome]} — ${reason}`;

  let hornInbox = input.call.hornInbox ?? createHornInbox();
  {
    const posted = postHorn(hornInbox, {
      kind: "liege-answer",
      title:
        outcome === "delay"
          ? `You delay ${input.call.liege.name}`
          : `You answer ${input.call.liege.name}: ${OUTCOME_LABELS[outcome]}`,
      body:
        outcome === "delay"
          ? `Delay clock: ${delayTurnsLeft} turn${delayTurnsLeft === 1 ? "" : "s"}. ${reason}`
          : `${OUTCOME_LABELS[outcome]} — ${troops} spears. ${reason}`,
      turn: input.turn ?? 0,
      season: input.season,
      actorName: "You",
      outcome,
      pending: outcome === "delay",
      turnsLeft: delayTurnsLeft ?? undefined,
    });
    hornInbox = posted.inbox;
  }

  return {
    direction: "liege-to-you",
    answers: [],
    playerAnswer,
    arms: armsAfter,
    hostOrigin: hostOriginAfter,
    stocks,
    supply,
    log,
    preview,
    toast,
    hornInbox,
    activeQuest: startedQuest,
  };
}

function playerChoiceReason(
  choice: CallOutcome,
  season: Season,
  assessed: ShowAssessment,
): string {
  switch (choice) {
    case "join":
      return "You answer in full — duty before the ledger.";
    case "delay":
      return season === "Winter"
        ? "You beg leave of winter roads — arrive when the thaw allows."
        : "You delay the column to settle stores first.";
    case "partial":
      return "You send a partial levy and keep spears on the seat.";
    case "refuse":
      return assessed.loyalty === "rebel-risk"
        ? "You refuse the horn — the oath frays in public."
        : "You refuse the call and keep the garrison home.";
  }
}

/**
 * Apply muster result onto call + ledger (immutable).
 * Pass `turn` so successful you-to-banners calls stamp once-per-turn.
 */
export function applyMuster(
  call: CallState,
  ledger: LedgerState,
  result: MusterResult,
  opts?: { liegeFavorOverride?: number; turn?: number },
): { call: CallState; ledger: LedgerState } {
  const liegeFavorOverride = opts?.liegeFavorOverride;
  const turn = opts?.turn;

  // Soft-fail (e.g. already called this turn): toast + log only, no arms/stocks burn
  if (result.blocked) {
    return {
      call: {
        ...call,
        lastToast: result.toast,
        lastCallLog: result.log.length
          ? [...result.log, ...call.lastCallLog].slice(0, 24)
          : call.lastCallLog,
      },
      ledger,
    };
  }

  const liege =
    result.playerAnswer && liegeFavorOverride === undefined
      ? {
          ...call.liege,
          favor: adjustFavorFromResult(call.liege.favor, result),
        }
      : liegeFavorOverride !== undefined
        ? { ...call.liege, favor: liegeFavorOverride }
        : call.liege;

  const totalsHint = (() => {
    const seats =
      result.bannerSeatsReplace && result.bannerSeats
        ? result.bannerSeats
        : call.bannerSeats;
    const t = seatArmsTotals({
      ...call,
      arms: result.arms,
      bannerSeats: seats,
    });
    const en =
      t.bannersInTransit > 0 ? ` · en route ${t.bannersInTransit}` : "";
    return `Garrison ${garrisonTotal(result.arms)} · host ${hostTotal(result.arms)}${en}`;
  })();

  // Sync seat chrome
  const seat = {
    ...ledger.seat,
    liegeHint: `${liege.name}, ${liege.title} · favor ${liege.favor}`,
    garrisonHint: totalsHint,
  };

  // Two independent horns:
  // - you-to-banners musters your vassals — must NOT clear Halric's open call
  // - liege-to-you closes pending unless the answer is delay (clock stays open)
  const answeredLiege = result.direction === "liege-to-you";
  const musteredBanners = result.direction === "you-to-banners";
  const delayedLiege =
    answeredLiege && result.playerAnswer?.outcome === "delay";
  const delayTurns =
    delayedLiege && result.playerAnswer?.delayTurnsLeft != null
      ? result.playerAnswer.delayTurnsLeft
      : null;

  let bannerSeats = call.bannerSeats;
  if (result.bannerSeatsReplace && result.bannerSeats) {
    bannerSeats = result.bannerSeats;
  } else if (result.bannerSeats && result.bannerSeats.length > 0) {
    const byKey = new Map(call.bannerSeats.map((s) => [s.key, s]));
    for (const s of result.bannerSeats) byKey.set(s.key, s);
    bannerSeats = [...byKey.values()];
  }

  let pendingBannerReplies = call.pendingBannerReplies ?? [];
  if (result.pendingBannerReplies) {
    if (result.pendingBannerRepliesReplace) {
      pendingBannerReplies = result.pendingBannerReplies;
    } else {
      const byKey = new Map(pendingBannerReplies.map((p) => [p.key, p]));
      for (const p of result.pendingBannerReplies) byKey.set(p.key, p);
      pendingBannerReplies = [...byKey.values()];
    }
  }

  const hornInbox = result.hornInbox ?? call.hornInbox ?? createHornInbox();

  return {
    call: {
      ...call,
      liege,
      arms: result.arms,
      hostOrigin:
        result.hostOrigin != null
          ? normalizeHostOrigin(result.hostOrigin)
          : call.hostOrigin,
      bannerSeats,
      pendingBannerReplies,
      hornInbox,
      lastCallLog: result.log,
      lastToast: result.toast,
      lastPreview: result.preview,
      lastAnswers: musteredBanners ? result.answers : call.lastAnswers,
      lastPlayerAnswer: answeredLiege
        ? (result.playerAnswer ?? null)
        : call.lastPlayerAnswer,
      // Delay keeps the horn open; other liege answers close it
      liegeCallPending: answeredLiege
        ? delayedLiege
        : call.liegeCallPending,
      liegeDelayTurnsLeft: answeredLiege
        ? delayTurns
        : call.liegeDelayTurnsLeft,
      callCount: call.callCount + 1,
      lastBannerCallTurn:
        musteredBanners && turn !== undefined
          ? turn
          : call.lastBannerCallTurn,
      activeQuest:
        result.activeQuest !== undefined ? result.activeQuest : call.activeQuest,
      oathAccepted: true,
    },
    ledger: {
      ...ledger,
      stocks: result.stocks,
      seat,
      lastTickLog: [...result.log, ...ledger.lastTickLog].slice(0, 16),
    },
  };
}

function adjustFavorFromResult(favor: number, result: MusterResult): number {
  if (!result.playerAnswer) return favor;
  const o = result.playerAnswer.outcome;
  if (o === "join") return Math.min(100, favor + JOIN_FAVOR_GAIN);
  if (o === "delay") return Math.max(0, favor - DELAY_FAVOR_HIT);
  if (o === "partial") return Math.max(0, favor - PARTIAL_FAVOR_HIT);
  return Math.max(0, favor - REFUSE_FAVOR_HIT);
}

/** Accept boot oath — names liege, unlocks call actions. */
export function acceptOath(call: CallState, ledger: LedgerState): {
  call: CallState;
  ledger: LedgerState;
} {
  const liege = call.liege;
  const seat = {
    ...ledger.seat,
    liegeHint: `${liege.name}, ${liege.title} · favor ${liege.favor}`,
    garrisonHint: `Garrison ${garrisonTotal(call.arms)} holds the seat · host ${hostTotal(call.arms)} (banner visitors)`,
    note: `Sworn to ${liege.name}. Capital only — call the banners when the horn sounds.`,
  };
  return {
    call: {
      ...call,
      oathAccepted: true,
      lastToast: `Oath sworn to ${liege.name}.`,
      lastCallLog: [
        `Oath: you kneel to ${liege.name}, ${liege.title}.`,
        `Favor ${liege.favor} · ${LOYALTY_LABELS[loyaltyBucket(liege.favor)]}.`,
        `Garrison ${garrisonTotal(call.arms)} holds the seat (Call never peels it); host empty until banners visit.`,
      ],
    },
    ledger: {
      ...ledger,
      seat,
      lastTickLog: [
        `Oath sworn to ${liege.name}.`,
        ...ledger.lastTickLog,
      ].slice(0, 16),
    },
  };
}

/** Liege posts a call (pending until you answer). Clears any stale delay clock. */
/** Named reasons the liege may sound the horn (rules AI, not LLM). */
export type LiegeCallReason =
  | "first-oath"
  | "idle-coax"
  | "quest-stale"
  | "host-idle"
  | "favor-low"
  | "season-pressure";

export type LiegeCallDecision = {
  should: boolean;
  reason: LiegeCallReason | null;
  /** Short HUD / test label */
  label: string | null;
};

/**
 * P4 Slice 5 — need-driven liege horns.
 * Replaces pure `turn % 3 === 0` spam with pressure + cooldown.
 * Never posts while a call is already pending or a quest is still open.
 */
export function shouldPostLiegeCall(
  call: CallState,
  turn: number,
  opts?: {
    /** Realm hunger tag from growth (optional pressure). */
    hunger?: "surplus" | "stable" | "hungry" | "starving" | null;
    /** Winter / late-year soft pressure. */
    season?: Season | null;
  },
): LiegeCallDecision {
  if (!call.oathAccepted) {
    return { should: false, reason: null, label: null };
  }
  if (call.liegeCallPending) {
    return { should: false, reason: null, label: null };
  }
  if (call.activeQuest && isQuestOpen(call.activeQuest)) {
    return { should: false, reason: null, label: null };
  }

  const last = typeof call.lastLiegeCallTurn === "number" ? call.lastLiegeCallTurn : -1;
  const since = last < 0 ? 999 : turn - last;
  // Hard cooldown: at least 2 turns between horns (never spam consecutive End turns)
  if (since < 2) {
    return { should: false, reason: null, label: null };
  }

  const favor = call.liege?.favor ?? 50;
  const host = hostTotal(normalizeArms(call.arms));
  const hunger = opts?.hunger ?? null;
  const season = opts?.season ?? null;

  // Priority order (first match wins)
  if (last < 0 && turn >= 1) {
    return {
      should: true,
      reason: "first-oath",
      label: "liege tests the oath",
    };
  }
  if (favor < 35 && since >= 2) {
    return {
      should: true,
      reason: "favor-low",
      label: "liege presses a shaky oath",
    };
  }
  if (host >= 8 && since >= 3) {
    return {
      should: true,
      reason: "host-idle",
      label: "liege wants the host on the road",
    };
  }
  if ((hunger === "hungry" || hunger === "starving") && since >= 3) {
    return {
      should: true,
      reason: "season-pressure",
      label: "realm hunger — liege musters for order",
    };
  }
  if (season === "Winter" && since >= 4) {
    return {
      should: true,
      reason: "season-pressure",
      label: "winter levy — liege sounds the horn",
    };
  }
  // Soft idle coax: only after a longer quiet stretch (not every 3rd turn)
  if (since >= 5) {
    return {
      should: true,
      reason: "idle-coax",
      label: "liege grows impatient",
    };
  }

  return { should: false, reason: null, label: null };
}

export function postLiegeCall(
  call: CallState,
  opts?: {
    turn?: number;
    season?: Season;
    /** Optional need-driven reason line (P4 Slice 5). */
    reasonLabel?: string | null;
  },
): CallState {
  const reasonBit = opts?.reasonLabel ? ` (${opts.reasonLabel})` : "";
  const toast = `${call.liege.name} sounds the horn — answer the call.${reasonBit}`;
  let hornInbox = call.hornInbox ?? createHornInbox();
  const turn = opts?.turn ?? 0;
  const posted = postHorn(hornInbox, {
    kind: "liege-call",
    title: `${call.liege.name} sounds the horn`,
    body: toast,
    turn,
    season: opts?.season,
    actorName: call.liege.name,
    pending: true,
  });
  hornInbox = posted.inbox;
  return {
    ...call,
    liegeCallPending: true,
    liegeDelayTurnsLeft: null,
    lastLiegeCallTurn: turn,
    lastToast: toast,
    hornInbox,
  };
}
